import { BaseStitchListStateWithSearch } from '@modules/common/types/BaseStitchListStateWithSearch';
import { SortBy } from '@modules/common/types/SortBy';
import { Like } from '@modules/common/types/like';
import { SortOrder } from '@modules/common/types/sort-order';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { AdvancedSearchState } from '@modules/search/types/advanced-search-state';
import { SheetsAppAdvancedFilters } from '@modules/search/types/sheets-app-advanced-filters';
import { StateService } from '@modules/settings/services/state.service';
import { StateKey } from '@modules/settings/types/state-key';
import { OnChange } from '@frontendlib/common/decorators/on-change';
import { type Sheet } from './sheet';

export type SheetListSortField = 'title' | 'createdAt' | 'updatedAt' | 'score' | 'sharedViaLinkAt';

export class SheetsListState extends BaseStitchListStateWithSearch<Sheet, SheetListSortField> {
  @OnChange('onPropertyChange')
  declare public sortBy?: SheetListSortField;
  @OnChange('onPropertyChange')
  declare public today?: boolean;

  constructor({
    searchService,
    stateService,
    stateKey,
    withAdvanced,
    defaultSort = { by: 'createdAt', order: SortOrder.DESC },
  }: {
    searchService?: AdvancedSearchService;
    stateService?: StateService;
    stateKey?: StateKey;
    withAdvanced?: boolean;
    defaultSort?: SortBy<SheetListSortField>;
  }) {
    super({ searchService, stateService, stateKey, withAdvanced, defaultSort });
  }

  protected override applySavedState(state?: Like<SheetsListState>) {
    super.applySavedState(state);
    // Session-only, never restored: the toggle is drawn in KP alone now, and KP list states carry no
    // `stateKey`. So wherever a saved `true` could still come from, the control that set it is no longer
    // drawn there — restoring one would date-filter a list whose menu offers nothing to turn it back
    // off, leaving "Reset to default" as the only way out. Sheet this is NOT "the toggle never worked":
    // in four sidebars it did (calendars, groups, folders, sheet-folders), because their chain never runs
    // `applyAdvancedFilters` and their menus gate it on `withScore` alone. See
    // devdoc/specs/today-filter.md, "Why it is not persisted".
    this.today = false;
  }

  public override onPropertyChange(attribute) {
    if ((attribute === 'from' && this.from) || (attribute === 'to' && this.to)) {
      this.today = false;
    }

    if (attribute === 'today' && this.today) {
      this.from = null;
      this.to = null;
    }

    super.onPropertyChange(attribute);
  }

  protected override syncState() {
    super.syncState({
      sortBy: this.sortBy,
    });
  }

  public override isDefault(): boolean {
    return super.isDefault() && !this.today;
  }

  protected override syncFromAS({ sheets }: AdvancedSearchState) {
    this.stitchedWith = sheets.stitched;
    this.from = sheets.dateRangeFrom;
    this.to = sheets.dateRangeTo;
  }

  protected override syncToAS(): void {
    const searchState = this.searchService.getStateSync();

    this.searchService.setState(
      new AdvancedSearchState({
        ...searchState,
        sheets: new SheetsAppAdvancedFilters({
          ...searchState.sheets,
          stitched: this.stitchedWith,
          dateRangeFrom: this.from,
          dateRangeTo: this.to,
        }),
      }),
    );
  }
}
